import * as B from '@blueprintjs/core';
import React from 'react';

import {ApiFeature} from 'app/modules/Remote/Feature/types';
import {formatFeatureName} from 'app/utils/featureUtils';

import {useFeaturesQuery} from '../hooks';
import {PropertyPicker} from './PropertyPicker';
import cs from './styles.module.scss';
import {ProjectOption, RunPlanDraft} from './types';

export const PropertiesStep: React.FC<{
  draft: RunPlanDraft;
  setDraft: (patch: Partial<RunPlanDraft>) => void;
  projectOptions: ProjectOption[];
}> = ({draft, setDraft, projectOptions}) => {
  const {data: features, isLoading: isFeaturesLoading} = useFeaturesQuery(
    Number(draft.selectedFcId)
  );

  const portfolioNameByFcId = React.useMemo(
    () => new Map(projectOptions.map((p) => [Number(p.fcId), p.name])),
    [projectOptions]
  );
  const selectedPortfolioCount = new Set(
    draft.selectedFeatures.map((f) => f.properties.featureCollectionId)
  ).size;

  const removeFeature = (feature: ApiFeature) =>
    setDraft({
      selectedFeatures: draft.selectedFeatures.filter(
        (f) => f.properties.id !== feature.properties.id
      ),
    });

  return (
    <div className={cs.step}>
      <B.FormGroup label="Portfolio" labelFor="portfolio-select" className={cs.portfolioField}>
        <B.HTMLSelect
          id="portfolio-select"
          value={draft.selectedFcId}
          onChange={(e) => setDraft({selectedFcId: e.currentTarget.value})}
          fill
        >
          {projectOptions.map((p) => (
            <option key={p.fcId} value={p.fcId}>
              {p.name}
            </option>
          ))}
        </B.HTMLSelect>
      </B.FormGroup>
      <B.FormGroup label="Properties">
        {isFeaturesLoading ? (
          <B.Spinner size={20} />
        ) : features ? (
          <PropertyPicker
            features={features}
            selected={draft.selectedFeatures}
            onChange={(selectedFeatures) => setDraft({selectedFeatures})}
          />
        ) : null}
      </B.FormGroup>
      {draft.selectedFeatures.length > 0 && (
        <div className={cs.selectedSummary}>
          <div className={cs.selectedSummaryHeader}>
            <span className={cs.selectionCount}>
              {draft.selectedFeatures.length} selected
              {selectedPortfolioCount > 1 && ` across ${selectedPortfolioCount} portfolios`}
            </span>
            <B.Button
              minimal
              small
              text="Clear selection"
              onClick={() => setDraft({selectedFeatures: []})}
            />
          </div>
          <div className={cs.selectedPills}>
            {draft.selectedFeatures.map((feature) => (
              <B.Tag
                key={feature.properties.id}
                minimal
                onRemove={() => removeFeature(feature)}
                // Names can repeat across portfolios, so say where each pill came from.
                title={portfolioNameByFcId.get(feature.properties.featureCollectionId)}
              >
                {formatFeatureName(feature)}
              </B.Tag>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
